import type { Timestamp } from '@bufbuild/protobuf/wkt'
import { debounce } from 'lodash'
import { ChevronRightIcon, Layers } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Alert } from '@/gen/inventory/v1/alert_pb'
import { Company } from '@/gen/inventory/v1/company_service_pb'
import { QuestionnaireStatus } from '@/gen/questionnaire/common/v1/questionnaire_status_pb'
import { type BaseQuestionnaire } from '@/gen/questionnaire/v2/model_pb'

import { SECTION_LABELS } from '@/const/label'

import { useTrackCallback } from '@/lib/analytics/events'
import { useHasPermissions } from '@/lib/app/check-permissions.hook'
import { useDrawerNavigationStore } from '@/lib/app/drawer-navigation.hook'
import { useFeatureFlagEnabled } from '@/lib/config/featureflag.ts'
import { UrlParam, useUrlParams } from '@/lib/url/url-param.hook'

import { countOpenOrOngoingAlerts } from '@/pages/alert-inventory/utils'
import { useAssessmentAnalytics } from '@/pages/assessment/analytics'
import { ASSESSMENT_TABS } from '@/pages/assessment/assessment-tabs/assessment-tabs'
import { BulkUpdateStatusModal } from '@/pages/assessment/assessment-tabs/bulk/bulk-update-status-modal'
import { SmartEvidenceRequestBanner } from '@/pages/assessment/assessment-tabs/smart-evidence-request/smart-evidence-request-banner'
import {
  ISSUES_TAB_DEFAULT_VISIBLE_FILTERS,
  getUnvalidatedControlsColumnDefs,
} from '@/pages/assessment/issues-column-defs'
import { AgAlertTable } from '@/pages/company-drawer/ag-alert-table'
import {
  AgControlTable,
  AgControlTableProps,
  type AgControlTableRef,
} from '@/pages/control/ag-grid-control/ag-control-table'
import type { ControlRow } from '@/pages/control/ag-grid-control/types'
import { getResolvedControlRowClassName } from '@/pages/control/ag-grid-control/utils/get-resolved-control-row-class-name'
import { getPreferredControlDrawerTab } from '@/pages/control/utils'

import { BulkActionsBar } from '@/components/bulk-actions-bar/bulk-actions-bar'
import { Button } from '@/components/lemmala-design-system/button/button'
import { AgDataTableTrackingContext } from '@/components/lemmala-design-system/table'
import { TextWithIcon } from '@/components/lemmala-design-system/text-with-icon/text-with-icon'

type IssuesTabProps = {
  assessmentId: string
  company: Company
  unvalidatedControls: ControlRow[]
  alerts: Alert[]
  smartEvidenceQuestionnaire?: BaseQuestionnaire
  dueDate?: Timestamp
}

type UnvalidatedControlsSectionProps = {
  controls: ControlRow[]
  columnDefs: ReturnType<typeof getUnvalidatedControlsColumnDefs>
  filterContext: string
  onVisibleRowOrderChange?: (ids: string[]) => void
  onSelectionChanged?: (ids: string[]) => void
  onSelectionEvent?: NonNullable<AgControlTableProps['selection']>['onSelectionEvent']
  controlTableRef?: React.Ref<AgControlTableRef>
  onSearchChange?: (query: string) => void
}

const ViewAllControlsLink = () => {
  const { updateParam } = useUrlParams()
  return (
    <div className='flex items-center py-4'>
      <Button
        onClick={() => updateParam(UrlParam.ASSESSMENT_TAB, ASSESSMENT_TABS.CONTROLS)}
        variant='link'
        className='h-auto p-0'
      >
        <TextWithIcon
          text='View all controls'
          icon={<ChevronRightIcon className='size-3' />}
          iconPosition='end'
        />
      </Button>
    </div>
  )
}

const trackingContext: AgDataTableTrackingContext = {
  eventName: 'third-party.assessment.issues.filter.apply',
}

const UnvalidatedControlsSection = ({
  controls,
  columnDefs,
  filterContext,
  onVisibleRowOrderChange,
  onSelectionChanged,
  onSelectionEvent,
  controlTableRef,
  onSearchChange,
}: UnvalidatedControlsSectionProps) => {
  const { updateParam, replaceParams } = useUrlParams()
  if (controls.length === 0) {
    return (
      <div className='flex flex-col items-center justify-center rounded-md border border-main-border-default bg-main-background-card py-10 text-center'>
        <div className='mb-1 font-semibold text-main-foreground-primary text-label-large'>
          No unvalidated controls
        </div>
        <div className='mb-6 max-w-sm text-main-foreground-secondary text-body-default'>
          You can now conclude this assessment or review validated controls before closing.
        </div>
        <Button
          variant='outline'
          size='sm'
          onClick={() => updateParam(UrlParam.ASSESSMENT_TAB, ASSESSMENT_TABS.CONCLUSION)}
        >
          Conclude assessment
        </Button>
      </div>
    )
  }

  return (
    <AgControlTable
      ref={controlTableRef}
      controls={controls}
      disableGroupBy
      disableViewSelection
      filterContext={filterContext}
      selection={{
        enabled: true,
        multiple: true,
        onSelectionChanged,
        onSelectionEvent,
      }}
      className='mt-3'
      hideEvidenceRequest={false}
      dynamicHeight={false}
      columnDefs={columnDefs}
      getRowClassName={getResolvedControlRowClassName}
      defaultVisibleFilters={ISSUES_TAB_DEFAULT_VISIBLE_FILTERS}
      rowHeight='double'
      toolbarClassName='mb-3'
      trackingContext={trackingContext}
      onSearchChange={onSearchChange}
      onRowClick={(control) => {
        replaceParams({
          update: [
            [UrlParam.CONTROL_ID, control.id],
            [UrlParam.CONTROL_TIMELINE_TAB, getPreferredControlDrawerTab(control)],
          ],
        })
      }}
      onVisibleRowOrderChange={onVisibleRowOrderChange}
    />
  )
}

export const IssuesTab = ({
  assessmentId,
  unvalidatedControls,
  alerts,
  smartEvidenceQuestionnaire,
  company,
  dueDate,
}: IssuesTabProps) => {
  const trackingContext = useAssessmentAnalytics()
  const { updateParam } = useUrlParams()
  const trackIssuesSearch = useTrackCallback('third-party.assessment.issues.search')

  const controlTableRef = useRef<AgControlTableRef>(null)
  const [selectedControlIds, setSelectedControlIds] = useState<string[]>([])
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false)

  const trackingProps = useMemo(
    () => ({
      company: company.profile?.name,
      company_id: company.id,
      assessment_id: assessmentId,
    }),
    [company, assessmentId],
  )

  const trackControlSelected = useTrackCallback('assessment.controls.control-selected')
  const trackAllSelected = useTrackCallback('assessment.controls.all-selected')
  const trackAllDeselected = useTrackCallback('assessment.controls.all-deselected')
  const trackSelectionCleared = useTrackCallback('assessment.controls.selection-cleared')
  const trackBulkUpdateClicked = useTrackCallback('assessment.controls.bulk-update-clicked')

  const handleSelectionEvent = useCallback(
    (type: 'row-selected' | 'all-selected' | 'all-deselected', count: number) => {
      if (type === 'row-selected') {
        trackControlSelected({ ...trackingProps, tab: 'issues', selected_count: count })
      } else if (type === 'all-selected') {
        trackAllSelected({ ...trackingProps, tab: 'issues', selected_count: count })
      } else {
        trackAllDeselected({ ...trackingProps, tab: 'issues' })
      }
    },
    [trackControlSelected, trackAllSelected, trackAllDeselected, trackingProps],
  )

  const clearSelection = useCallback(() => {
    trackSelectionCleared({ ...trackingProps, selected_count: selectedControlIds.length })
    controlTableRef.current?.clearSelection()
    setSelectedControlIds([])
  }, [trackSelectionCleared, trackingProps, selectedControlIds.length])

  const handleUpdateStatus = useCallback(() => {
    trackBulkUpdateClicked({
      ...trackingProps,
      tab: 'issues',
      selected_count: selectedControlIds.length,
    })
    setIsUpdateModalOpen(true)
  }, [trackBulkUpdateClicked, trackingProps, selectedControlIds.length])

  const debouncedTrackIssuesSearch = useMemo(
    () =>
      debounce((query: string) => {
        if (!trackingContext) {
          return
        }

        trackIssuesSearch({
          ...trackingContext,
          query,
        })
      }, 300),
    [trackIssuesSearch, trackingContext],
  )

  useEffect(() => {
    return () => {
      debouncedTrackIssuesSearch.cancel()
    }
  }, [debouncedTrackIssuesSearch])

  // Column definitions
  const hasManagePermissions = useHasPermissions(['base.manage'])
  const isUnpublished =
    smartEvidenceQuestionnaire != null &&
    smartEvidenceQuestionnaire.status !== QuestionnaireStatus.ONGOING &&
    smartEvidenceQuestionnaire.status !== QuestionnaireStatus.SUBMITTED
  const unvalidatedControlsColumns = useMemo(
    () =>
      getUnvalidatedControlsColumnDefs(hasManagePermissions, isUnpublished, unvalidatedControls),
    [hasManagePermissions, isUnpublished, unvalidatedControls],
  )
  const { isEnabled: isSmartEvidenceRequestBannerEnabled } = useFeatureFlagEnabled(
    'smart-evidence-request-banner',
  )

  const handleVisibleRowOrderChange = useDrawerNavigationStore()

  const unvalidatedCount = unvalidatedControls.length
  // Same count as the overview Issues card; the table below only shows open and ongoing alerts.
  const openAlertsCount = useMemo(() => countOpenOrOngoingAlerts(alerts), [alerts])

  const onClickAlert = useCallback(
    (alertId: string) => {
      updateParam(UrlParam.ALERT_ID, alertId)
    },
    [updateParam],
  )

  return (
    <>
      <div className='flex flex-col gap-0 bg-main-background-muted pb-6'>
        <div className='flex flex-col'>
          <div className='flex flex-col rounded-md border border-main-border-default bg-main-background-subtle-gray p-4'>
            {isSmartEvidenceRequestBannerEnabled && unvalidatedCount > 0 && (
              <SmartEvidenceRequestBanner
                assessmentId={assessmentId}
                company={company}
                smartEvidenceQuestionnaire={smartEvidenceQuestionnaire}
                readonly={!hasManagePermissions}
                dueDate={dueDate}
              />
            )}

            <h3 className='mt-3 text-main-foreground-primary text-heading-xxsmall'>
              Unvalidated controls
            </h3>
            <UnvalidatedControlsSection
              controls={unvalidatedControls}
              columnDefs={unvalidatedControlsColumns}
              filterContext={`issues_assessment_${assessmentId}`}
              onVisibleRowOrderChange={handleVisibleRowOrderChange}
              onSelectionChanged={setSelectedControlIds}
              onSelectionEvent={handleSelectionEvent}
              controlTableRef={controlTableRef}
            />

            <ViewAllControlsLink />
          </div>

          {alerts.length > 0 && (
            <div className='flex flex-col gap-4 pt-6'>
              <h3 className='text-main-foreground-primary text-heading-xxsmall'>
                {SECTION_LABELS.alerts}
              </h3>
              <AgAlertTable
                dataSource={alerts}
                onClick={onClickAlert}
                filterOnlyOpenAndOngoing={openAlertsCount > 0}
                showFilterBar
                containerClassName='h-96'
              />
            </div>
          )}
        </div>
      </div>

      {selectedControlIds.length > 0 && (
        <BulkActionsBar
          selectedCount={selectedControlIds.length}
          entityLabel='control'
          actions={[
            {
              id: 'update-status',
              label: 'Update status',
              icon: <Layers className='size-4' />,
              onClick: handleUpdateStatus,
            },
          ]}
          onClose={clearSelection}
        />
      )}

      {isUpdateModalOpen && (
        <BulkUpdateStatusModal
          onClose={() => setIsUpdateModalOpen(false)}
          selectedControlIds={selectedControlIds}
          assessmentId={assessmentId}
          tab='issues'
          companyId={company.id}
        />
      )}
    </>
  )
}
